import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { collectionKeys } from "@/lib/query-keys";
import { normalizeRecordImageShape } from "@/offline/record-shape";
import type { PaginatedResponse, ReportCollectionItem } from "@/types/api";

const PAGE_SIZE = 60;

/**
 * A collection's contents, newest first.
 *
 * Tombstones come back in the list rather than being filtered server-side: an
 * item deleted from the project has to stay visible, or a report that quietly
 * comes back shorter is nobody's fault and nobody's problem until a client
 * notices.
 */
export function useCollectionItems(projectId: string, collectionId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useInfiniteQuery({
    queryKey: collectionKeys.items(projectId, collectionId),
    queryFn: async ({ pageParam }) => {
      const page = await fetchWithAuth<PaginatedResponse<ReportCollectionItem>>(
        `/reports/3.0/projects/${projectId}/collections/${collectionId}/items/` +
          `?page=${pageParam}&page_size=${PAGE_SIZE}`,
      );
      // An item's record arrives in the v2.0 `images[]` wire (`*_url` only).
      // Normalize it to the canonical `{ image, thumbnail }` shape, as the
      // Photos page does, or every tile and row paints an empty src and the
      // record viewer opens on no photo — so does not open. See record-shape.
      return {
        ...page,
        results: page.results.map((item) =>
          item.record
            ? { ...item, record: normalizeRecordImageShape(item.record) }
            : item,
        ),
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    select: (data) => ({
      items: data.pages.flatMap((page) => page.results),
      totalCount: data.pages[0]?.count ?? 0,
    }),
    staleTime: 30_000,
  });
}
